import { useApi, useCaller } from '@/hooks/api.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import {
    setCashbackDetailAction,
    setCashbackLoading,
    setListCashbackAction
} from '@/redux/slicers/cashback.slice';
import { setMessageAction } from '@/redux/slicers/api-message.slice';
import { generateUrlParams } from '@/utils/common';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

type GetCashbackDetailParams = {
    accrualId: string;
    page?: number;
    pageSize?: number;
    append?: boolean;
};

export const useCashback = () => {
    const api = useApi('');
    const caller = useCaller();
    const dispatch = useDispatch();
    const customerId = useRootSelector((state) => state.auth.customerId);

    const getAllCashback = useCallback(async () => {
        const { content, succeeded } = await caller(
            () => api.get(`/v2/api/customer/${customerId}/cashback/overview`),
            {
                loadingKey: 'get-all-cashback',
            },
        );

        if (succeeded) {
            dispatch(setListCashbackAction({ data: content }));
        }
    }, [caller, api, dispatch]);

    const getCashbackDetail = useCallback(
        async ({ accrualId, page = 1, pageSize = 10, append = false }: GetCashbackDetailParams) => {
            // Check if customerId exists before making API call
            if (!customerId) {
                dispatch(setCashbackLoading(false));
                return;
            }

            const queryParams: { [key: string]: string | undefined } = {
                page: page.toString(),
                pageSize: pageSize.toString(),
            };

            // Remove undefined values
            Object.keys(queryParams).forEach((key) => {
                if (queryParams[key] === undefined || queryParams[key] === '') {
                    delete queryParams[key];
                }
            });

            const urlParams = generateUrlParams(queryParams);

            dispatch(setCashbackLoading(true));

            const { content, succeeded } = await caller(
                () =>
                    api.get(
                        `/v2/api/customer/${customerId}/cashback/accruals/${accrualId}?${urlParams}`,
                    ),
                {
                    loadingKey: 'get-cashback-detail',
                },
            );

            if (succeeded && content) {
                dispatch(
                    setCashbackDetailAction({
                        accrual: content.accrual,
                        transactions: content.transactions.items,
                        pagination: {
                            Page: content.transactions.page,
                            totalPages: content.transactions.totalPages,
                            totalElements: content.transactions.totalCount,
                            Size: content.transactions.pageSize,
                        },
                        append,
                    }),
                );
            }

            dispatch(setCashbackLoading(false));
        },
        [caller, api, dispatch, customerId],
    );



    const getCashbackAccruals = useCallback(
        async (page: number = 1, pageSize: number = 10) => {
            const { content, succeeded } = await caller(
                () => api.get(`/v2/api/customer/${customerId}/cashback/accruals?page=${page}&pageSize=${pageSize}`),
                {
                    loadingKey: 'get-cashback-accruals',
                },
            );

            return { content, succeeded };
        },
        [caller, api, customerId],
    );

    const getCashbackRedemptions = useCallback(
        async (page: number = 1, pageSize: number = 10) => {
            const { content, succeeded } = await caller(
                () => api.get(`/v2/api/customer/${customerId}/cashback/redemptions?page=${page}&pageSize=${pageSize}`),
                {
                    loadingKey: 'get-cashback-redemptions',
                },
            );

            return { data: content, succeeded };
        },
        [caller, api, customerId],
    );

    const withdrawCashback = useCallback(
        async (amount: number, destinationVirtualAccountId: string, idempotencyKey: string) => {
            const response = await caller(
                () =>
                    api.post(`/v2/api/customer/${customerId}/cashback/withdraw`, {
                        amount,
                        destinationVirtualAccountId,
                    }, {
                        headers: {
                            'X-Idempotency-Key': idempotencyKey,
                        },
                    }),
                {
                    loadingKey: 'withdraw-cashback',
                },
            );

            if (response?.succeeded) {
                const message = response?.messages?.[0] ?? 'Cashback withdrawn successfully';
                dispatch(setMessageAction(['global', { type: 'success', messages: [message] }]));
            }

            return response;
        },
        [caller, api, dispatch, customerId],
    );

    return {
        getAllCashback,
        getCashbackDetail,
        getCashbackAccruals,
        getCashbackRedemptions,
        withdrawCashback,
    };
}
